import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'
import Container from './Container'
import FadeToggle, { MoveUp } from './FadeToggle'
import ScrollView, { ScrollViewRef } from './ScrollView'
import Title from './Title'

const OverlayWrap = styled(FadeToggle)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  transform: translateY(0px);
  z-index: 99;
  pointer-events: all;
`

const InnerWrap = styled(MoveUp)`
  height: 100%;
`

export type OverlaySectionProps = React.PropsWithChildren<{
  $title?: string
  $subline?: string
  $bigTitle?: boolean
  $fluid?: boolean
}>
export const OverlaySection = styled.div.attrs<OverlaySectionProps>(
  ({ children, $title, $subline, $bigTitle, $fluid }) => ({
    className: 'section',
    children: (
      <Container $fluid={$fluid}>
        {!!$title && (
          <Title subline={$subline} bigTitle={$bigTitle}>
            {$title}
          </Title>
        )}
        {children}
      </Container>
    )
  })
)<OverlaySectionProps>`
  border-bottom-color: #b2b2b2;
  padding: 100px 0 50px;

  &:last-child {
    border-bottom: none;
    min-height: calc(100vh - 55px);
  }

  ${Title} {
    margin-bottom: ${({ $bigTitle }) => ($bigTitle ? '60px' : '0')};
  }
`

export type OverlaySubsectionProps = React.PropsWithChildren<{
  title?: string
}>
export const OverlaySubsection = styled.div.attrs<OverlaySubsectionProps>(({ title, children }) => ({
  children: (
    <>
      {!!title && <h2>{title}</h2>}
      {children}
    </>
  ),
  title: ''
}))<OverlaySubsectionProps>`
  margin-bottom: 50px;
`

type OverlayProps = React.PropsWithChildren<{
  visible?: boolean
  shaded?: boolean
  className?: string
  currentSection?: string
  setCurrentSection?: (nextSection: string) => unknown
}>
export type OverlayRef = {
  scrollToSection: (sectionName: string, animate?: boolean) => void
}
const Overlay = styled(
  React.forwardRef<OverlayRef, OverlayProps>(function Overlay(
    { children, visible, className, currentSection, setCurrentSection },
    ref
  ) {
    const scrollView = useRef<ScrollViewRef>(null)

    const scrollToSection = useCallback<OverlayRef['scrollToSection']>(
      (sectionName, animate) => {
        const target = scrollView.current?.getChildById(sectionName)
        if (!target) {
          return
        }
        scrollView.current?.scrollTo({ y: target.offsetTop }, animate)
        if (currentSection !== sectionName) {
          setCurrentSection?.(sectionName)
        }
      },
      [currentSection, setCurrentSection]
    )

    const getCurrentSectionFromDOM = useCallback((): HTMLElement | undefined => {
      const scrollElement = scrollView.current?.getScrollElement()
      if (!scrollElement) {
        return
      }
      const { height } = scrollElement.getBoundingClientRect()
      const limit = height / 4
      const children = scrollView.current?.getChildren().filter((x) => !!x.id)
      if (!children || !children.length) {
        return
      }
      const tops = children.map((x) => x.getBoundingClientRect().top)
      const firstVisible = tops.findIndex((top) => top > 0)
      if (firstVisible < 0) {
        return children[children.length - 1]
      }
      if (tops[firstVisible] > limit) {
        return children[Math.max(0, firstVisible - 1)]
      }
      return children[firstVisible]
    }, [])

    const handleScroll = useCallback(() => {
      const scrollElement = scrollView.current?.getScrollElement()
      if (!scrollElement || $(scrollElement).is(':animated')) {
        return
      }
      const currentSection = getCurrentSectionFromDOM()
      if (currentSection) {
        setCurrentSection?.(currentSection.id)
      }
    }, [getCurrentSectionFromDOM, setCurrentSection])

    useEffect(() => {
      window.addEventListener('resize', handleScroll)
      return () => window.removeEventListener('reset', handleScroll)
    }, [handleScroll])

    // useEffect(() => {
    //   const currentSectionEl = getCurrentSectionFromDOM()
    //   if (!currentSection || currentSectionEl?.id === currentSection) {
    //     return
    //   }
    //   scrollToSection(currentSection, true)
    //   const onResize = () => scrollToSection(currentSection, false)
    //   window.addEventListener('resize', onResize, { passive: true })
    //   return window.removeEventListener('resize', onResize)
    // }, [scrollToSection, currentSection, getCurrentSectionFromDOM])

    useImperativeHandle(
      ref,
      () => ({
        scrollToSection
      }),
      [scrollToSection]
    )

    return (
      <OverlayWrap visible={visible} className={className}>
        <InnerWrap>
          <ScrollView disableWindowScroll onScroll={handleScroll} ref={scrollView}>
            {children}
          </ScrollView>
        </InnerWrap>
      </OverlayWrap>
    )
  })
)<OverlayProps>`
  background-color: ${({ shaded }) => (shaded ? '#F2F2F2' : 'white')};
`

export default Overlay
